/**
 * Home-screen / PWA app icon, user-selectable.
 *
 * The pixels live in public/icons/ and are generated by
 * scripts/icons/build-icons.py from the committed SVG sources in scripts/icons/src/. Each variant
 * also has its own static manifest (public/manifest-<key>.webmanifest) because
 * wrangler.jsonc routes only /api/* through the Worker -- everything else comes
 * straight from the assets bucket, so a manifest cannot be generated per request.
 * Selecting an icon therefore just repoints the two <link> tags in index.html.
 *
 * IMPORTANT, and surfaced to the user in Settings: iOS and Android both snapshot
 * the icon at the moment the user taps "Add to Home Screen" / "Install". Changing
 * this setting afterwards does NOT update an icon that is already on a home
 * screen -- the shortcut has to be removed and re-added.
 */
import type { UiPrefsSlice } from "@shared/ui-prefs";

// Pinned to the shared slice allowlist: renaming the key here without adding it
// to UI_PREFS_SLICES would make every icon save a 400, so the type says no.
export const APP_ICON_PREF_KEY: UiPrefsSlice = "app_icon";

/** Shipped when the user has never chosen (and what index.html hard-codes). */
export const DEFAULT_APP_ICON = "B";

export interface AppIconVariant {
  key: string;
  label: string;
  /** One line on what the tile actually looks like, shown under the swatch. */
  note: string;
}

export const APP_ICONS: AppIconVariant[] = [
  { key: "B", label: "Webbing diagonal", note: "Navy with the webbing sweeping across the base. Two-tone mark." },
  { key: "C", label: "Low-poly field", note: "Full-bleed faceted blue field. White mark." },
  { key: "C2", label: "Webbing, deep", note: "Deeper navy, webbing dropped into the lower-right diagonal." },
  { key: "C3", label: "Low-poly diagonal", note: "Faceted mesh cutting a diagonal through the tile." },
  { key: "A", label: "Solid navy", note: "No texture — just the mark on flat navy." },
];

export function isAppIconKey(key: unknown): key is string {
  return typeof key === "string" && APP_ICONS.some((v) => v.key === key);
}

/** Preview/swatch source. 512 is the only size worth showing in a picker. */
export function appIconPreview(key: string): string {
  return `/icons/icon-${key}-512.png`;
}

/**
 * Repoint the live document at `key`'s icon set.
 *
 * Safari re-reads apple-touch-icon when the share sheet is opened, so mutating the
 * existing <link> in place is enough — no reload needed. The manifest link is
 * swapped the same way for Chrome/Android.
 */
export function applyAppIcon(key: string): void {
  if (!isAppIconKey(key)) return;

  const set = (selector: string, href: string) => {
    const el = document.head.querySelector<HTMLLinkElement>(selector);
    if (el && el.getAttribute("href") !== href) el.setAttribute("href", href);
  };

  set('link[rel="apple-touch-icon"]', `/icons/icon-${key}-180.png`);
  set('link[rel="icon"]', `/icons/icon-${key}-192.png`);
  set('link[rel="manifest"]', `/manifest-${key}.webmanifest`);
}
